import { useNavigate, useLocation } from "react-router";
import { useLocale } from "./useLocale";
import { localePath } from "./localePath";

/**
 * `useSwitchLocale()` — returns a function that navigates to the SAME page under a
 * different locale, preserving the sub-path + query + hash and only swapping the
 * leading locale prefix (`/nl/pricing` → `/en/pricing`).
 *
 * Switching locale IS a URL change, so content stays in sync with the route (a
 * client-only toggle that left the URL alone would desync copy from the path).
 * Shared by the market switchers and the language switcher (both in `SiteNav`)
 * so the navigation rule lives in exactly one place.
 *
 * This is also the ONLY place that writes `pref_locale` — the cookie the S10 edge
 * function honours over geo. It must record a deliberate choice and nothing else:
 * `Layout` used to set it for whatever locale happened to render, which pinned a
 * visitor to the locale of their first-ever visit and made geo-detection dead on
 * every subsequent one (DIT-264 QA).
 */
export function useSwitchLocale(): (code: string) => void {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  return (code: string): void => {
    if (code === locale) return;
    document.cookie = `pref_locale=${code}; path=/; max-age=31536000; SameSite=Lax`;
    // Strip the active locale prefix → bare sub-path, then re-prefix with the target.
    const prefix = `/${locale}`;
    const bare = location.pathname.startsWith(prefix)
      ? location.pathname.slice(prefix.length) || "/"
      : location.pathname;
    navigate(localePath(code, bare) + location.search + location.hash);
  };
}
